/**
 * fullscreen-canvas-patterns.js — gamepad-navigable color/pattern sweeps for
 * screen-test class tools (dead-pixel, color uniformity, brightness, geometry).
 *
 * The Steam Deck screen-test, generic dead-pixel-test and monitor-color-test all
 * need the same primitive: cover the entire viewport with a solid color or
 * pattern, advance/retreat with input, exit on Escape/B-button. Sharing this
 * eliminates 3 copies of fullscreen + key/gamepad-poll plumbing.
 *
 * Public API (window.FullscreenPatterns):
 *   .start({patterns, host, onChange, onExit, gamepadIndex})
 *      patterns: [{id, css, label}] OR [{id, draw(ctx, w, h), label}]
 *      host:     element to mount fullscreen into (defaults to document.body)
 *      onChange: (idx, pattern) => void
 *      onExit:   () => void
 *      gamepadIndex: which gamepad index to listen to (default 0)
 *
 * Returns a controller:
 *   { next(), prev(), exit(), index, total }
 *
 * Input mapping:
 *   Mouse / touch / Enter / Space / D-pad-Right / A-button → next()
 *   Backspace / D-pad-Left → prev()
 *   Escape / B-button → exit()
 *
 * Why a class, not just a helper: the controller owns rAF loops for gamepad
 * polling, pointer-event listeners on a fullscreen overlay, and the pattern-
 * advance state. A consumer tool wants to call `.exit()` from cancellation
 * or from a "Stop" button without redoing the wiring.
 */
(function(){
    'use strict';

    if (window.FullscreenPatterns && !window.FullscreenPatterns.__isStub) return;

    var STYLE_INJECTED = false;
    function _injectStyle() {
        if (STYLE_INJECTED) return;
        STYLE_INJECTED = true;
        var s = document.createElement('style');
        s.textContent = [
            '.fcp-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;background:#000;cursor:none;display:flex;align-items:center;justify-content:center;overflow:hidden}',
            '.fcp-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}',
            '.fcp-bar{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);background:rgba(0,0,0,.6);color:#fff;padding:10px 16px;border-radius:10px;font:600 13px/1.4 system-ui,sans-serif;display:flex;gap:14px;align-items:center;cursor:default}',
            '.fcp-bar small{opacity:.7;font-weight:500}',
            '.fcp-bar button{background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);color:#fff;padding:6px 12px;border-radius:8px;font:inherit;cursor:pointer}',
            '.fcp-bar button:hover{background:rgba(255,255,255,.2)}'
        ].join('');
        document.head.appendChild(s);
    }

    function _applyPattern(canvas, pattern) {
        if (!canvas || !pattern) return;
        var w = canvas.width = canvas.clientWidth * (window.devicePixelRatio || 1);
        var h = canvas.height = canvas.clientHeight * (window.devicePixelRatio || 1);
        if (typeof pattern.draw === 'function') {
            var ctx = canvas.getContext('2d');
            if (ctx) pattern.draw(ctx, w, h);
        } else if (typeof pattern.css === 'string') {
            // Solid color via canvas fillRect (matches DPR for crispness).
            var ctx2 = canvas.getContext('2d');
            if (ctx2) {
                ctx2.fillStyle = pattern.css;
                ctx2.fillRect(0, 0, w, h);
            } else {
                canvas.style.background = pattern.css;
            }
        }
    }

    function start(opts) {
        opts = opts || {};
        var patterns = opts.patterns || [];
        if (!patterns.length) throw new Error('FullscreenPatterns: patterns[] required');
        _injectStyle();

        var host = opts.host || document.body;
        var idx = 0;
        var aborted = false;

        var overlay = document.createElement('div');
        overlay.className = 'fcp-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-label', ((window.__commonStrings||{}).js_screen_test_pattern || "Screen test pattern"));
        var canvas = document.createElement('canvas');
        canvas.className = 'fcp-canvas';
        overlay.appendChild(canvas);

        var bar = document.createElement('div');
        bar.className = 'fcp-bar';
        var counter = document.createElement('span');
        var label = document.createElement('small');
        var btnPrev = document.createElement('button'); btnPrev.textContent = '◀';
        var btnNext = document.createElement('button'); btnNext.textContent = '▶';
        var btnExit = document.createElement('button'); btnExit.textContent = '✕';
        bar.append(btnPrev, counter, label, btnNext, btnExit);
        overlay.appendChild(bar);

        host.appendChild(overlay);

        function _refresh() {
            counter.textContent = (idx + 1) + ' / ' + patterns.length;
            label.textContent = patterns[idx].label || patterns[idx].id || '';
            _applyPattern(canvas, patterns[idx]);
            if (typeof opts.onChange === 'function') opts.onChange(idx, patterns[idx]);
        }

        function next() {
            idx = (idx + 1) % patterns.length;
            _refresh();
        }
        function prev() {
            idx = (idx - 1 + patterns.length) % patterns.length;
            _refresh();
        }
        function exit() {
            if (aborted) return;
            aborted = true;
            cancelAnimationFrame(rafId);
            document.removeEventListener('keydown', _onKey);
            window.removeEventListener('resize', _refresh);
            try { overlay.remove(); } catch (_) {}
            if (typeof opts.onExit === 'function') opts.onExit();
        }

        function _onKey(e) {
            if (e.key === 'Escape') { exit(); e.preventDefault(); return; }
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') { next(); e.preventDefault(); return; }
            if (e.key === 'Backspace' || e.key === 'ArrowLeft') { prev(); e.preventDefault(); return; }
        }

        // Gamepad poll — shared, debounced to one event per button-down transition.
        var prevPad = null;
        var rafId = 0;
        function _poll() {
            if (aborted) return;
            try {
                var pads = navigator.getGamepads ? navigator.getGamepads() : [];
                var pi = (typeof opts.gamepadIndex === 'number') ? opts.gamepadIndex : 0;
                var pad = pads[pi];
                if (pad) {
                    // Standard mapping: 0=A 1=B; 12=Up 13=Down 14=Left 15=Right
                    var aBtn = pad.buttons[0] && pad.buttons[0].pressed;
                    var bBtn = pad.buttons[1] && pad.buttons[1].pressed;
                    var dRight = pad.buttons[15] && pad.buttons[15].pressed;
                    var dLeft = pad.buttons[14] && pad.buttons[14].pressed;
                    var prevA = prevPad && prevPad.aBtn;
                    var prevB = prevPad && prevPad.bBtn;
                    var prevR = prevPad && prevPad.dRight;
                    var prevL = prevPad && prevPad.dLeft;
                    if ((aBtn && !prevA) || (dRight && !prevR)) next();
                    else if (dLeft && !prevL) prev();
                    else if (bBtn && !prevB) { exit(); return; }
                    prevPad = { aBtn:aBtn, bBtn:bBtn, dRight:dRight, dLeft:dLeft };
                } else {
                    prevPad = null;
                }
            } catch (_) {}
            rafId = requestAnimationFrame(_poll);
        }

        document.addEventListener('keydown', _onKey);
        window.addEventListener('resize', _refresh);
        overlay.addEventListener('click', function(e) {
            if (e.target === btnExit) exit();
            else if (e.target === btnPrev) prev();
            else if (e.target === btnNext) next();
            else if (e.target === overlay || e.target === canvas) next();
        });

        _refresh();
        rafId = requestAnimationFrame(_poll);

        return {
            next: next,
            prev: prev,
            exit: exit,
            get index() { return idx; },
            get total() { return patterns.length; }
        };
    }

    var FullscreenPatterns = { start: start };
    Object.defineProperty(FullscreenPatterns, '__isStub', { value: false });
    window.FullscreenPatterns = FullscreenPatterns;
})();
